import React, { useEffect, useState, useCallback } from "react";
import { Table, Spinner, Button, Form, Row, Col, Badge, Alert as BsAlert } from "react-bootstrap";
import SafetyDotazioniService from "../../../services/safety/SafetyDotazioniService";
import LavoratoreSearchPicker from "../LavoratoreSearchPicker/LavoratoreSearchPicker";

const EMPTY = {
    id_tipo_dot: "",
    id_lavoratore: "",
    titolo: "",
    marca: "",
    modello: "",
    numero_serie: "",
    calibro: "",
    ubicazione: "",
    stato_operativo: "OPERATIVO",
    data_assegnazione: "",
    data_ultima_revisione: "",
    data_scadenza: "",
    periodicita_mesi: "",
    note: "",
};

const STATI_OP = [
    { value: "OPERATIVO", label: "Operativo" },
    { value: "IN_REVISIONE", label: "In revisione" },
    { value: "RITIRATO", label: "Ritirato" },
    { value: "SMALTITO", label: "Smaltito" },
];

function fmtDate(d) {
    if (!d) return "—";
    const s = String(d).substring(0, 10);
    const p = s.split("-");
    if (p.length !== 3) return s;
    return `${p[2]}/${p[1]}/${p[0]}`;
}

function groupByCategoria(tipi) {
    const groups = {};
    tipi.forEach(t => {
        const cat = t.categoria || "Altri";
        if (!groups[cat]) groups[cat] = [];
        groups[cat].push(t);
    });
    return Object.entries(groups);
}

function statoBadge(stato, giorni) {
    const map = {
        SCADUTO:      { bg: "danger",  text: "light", label: "Scaduto" },
        IN_SCADENZA:  { bg: "warning", text: "dark",  label: "In scadenza" },
        VALIDO:       { bg: "success", text: "light", label: "Valido" },
        PERMANENTE:   { bg: "light",   text: "dark",  label: "Senza scadenza" },
    };
    const m = map[stato] || map.PERMANENTE;
    return (
        <span>
            <Badge bg={m.bg} text={m.text} className={m.bg === "light" ? "border" : undefined}>{m.label}</Badge>
            {typeof giorni === "number" && (stato === "SCADUTO" || stato === "IN_SCADENZA") && (
                <span className="text-muted small ms-1">
                    ({giorni < 0 ? `${Math.abs(giorni)} gg fa` : `tra ${giorni} gg`})
                </span>
            )}
        </span>
    );
}

/**
 * DotazioniPanel — registro armi, giubbotti, radio e dotazioni operative.
 *
 * Props:
 *  - idAzienda: number (richiesto per vista azienda / creazione)
 *  - idLavoratore: number opzionale (filtra o fissa assegnazione)
 *  - settore: string opzionale
 *  - soloLavoratore: boolean — se true, mostra solo dotazioni del lavoratore
 */
export default function DotazioniPanel({ idAzienda, idLavoratore, settore, soloLavoratore = false }) {
    const [tipi, setTipi] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [msg, setMsg] = useState(null);
    const [show, setShow] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);

    const loadTipi = useCallback(async () => {
        if (!idAzienda && !idLavoratore) { setTipi([]); return; }
        try {
            const resp = await SafetyDotazioniService.getTipi(idAzienda || "", idLavoratore || "", settore || "");
            const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
            if (Array.isArray(data)) {
                setTipi(data);
            } else if (data?.message || data?.error) {
                setTipi([]);
                setError(data.message || data.error);
            } else {
                setTipi([]);
            }
        } catch (err) {
            setTipi([]);
            const msg = err?.message || err?.error?.message || "";
            setError(
                msg.includes("NetworkError") || msg.includes("Failed to fetch")
                    ? "API non raggiungibile. Verifica che il backend PHP sia deployato (SafetyDotazioniController + Service), auth.json aggiornato e index.php con il require."
                    : (msg || "Impossibile caricare il catalogo dotazioni. Verifica SQL e deploy backend.")
            );
        }
    }, [idAzienda, idLavoratore, settore]);

    const loadItems = useCallback(async () => {
        if (soloLavoratore || idLavoratore) {
            if (!idLavoratore) { setItems([]); return; }
            setLoading(true);
            setError(null);
            try {
                const resp = await SafetyDotazioniService.byLavoratore(idLavoratore);
                const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
                setItems(Array.isArray(data) ? data : []);
            } catch (err) {
                setError(err?.message || "Errore nel caricamento delle dotazioni.");
            } finally {
                setLoading(false);
            }
            return;
        }
        if (!idAzienda) { setItems([]); return; }
        setLoading(true);
        setError(null);
        try {
            const resp = await SafetyDotazioniService.byAzienda(idAzienda);
            const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
            setItems(Array.isArray(data) ? data : []);
        } catch (err) {
            const msg = err?.message || err?.error?.message || "";
            setError(
                msg.includes("NetworkError") || msg.includes("Failed to fetch")
                    ? "API non raggiungibile (/safety/dotazioni/*). Controlla deploy backend e auth.json."
                    : (msg || "Errore nel caricamento del registro dotazioni.")
            );
        } finally {
            setLoading(false);
        }
    }, [idAzienda, idLavoratore, soloLavoratore]);

    useEffect(() => { loadTipi(); }, [loadTipi]);
    useEffect(() => { loadItems(); }, [loadItems]);

    const openForm = () => {
        setForm({
            ...EMPTY,
            id_lavoratore: soloLavoratore && idLavoratore ? String(idLavoratore) : "",
        });
        setFile(null);
        setError(null);
        setMsg(null);
        setShow(true);
    };

    const submit = async () => {
        const azId = idAzienda;
        if (!azId) return;
        if (!form.id_tipo_dot && !form.titolo) {
            setError("Seleziona un tipo o indica una descrizione.");
            return;
        }
        setSaving(true);
        setError(null);
        setMsg(null);
        try {
            const fd = new FormData();
            fd.append("id_azienda", azId);
            if (form.id_tipo_dot)           fd.append("id_tipo_dot", form.id_tipo_dot);
            if (form.id_lavoratore)         fd.append("id_lavoratore", form.id_lavoratore);
            if (form.titolo)                fd.append("titolo", form.titolo);
            if (form.marca)                 fd.append("marca", form.marca);
            if (form.modello)               fd.append("modello", form.modello);
            if (form.numero_serie)          fd.append("numero_serie", form.numero_serie);
            if (form.calibro)               fd.append("calibro", form.calibro);
            if (form.ubicazione)            fd.append("ubicazione", form.ubicazione);
            if (form.stato_operativo)       fd.append("stato_operativo", form.stato_operativo);
            if (form.data_assegnazione)     fd.append("data_assegnazione", form.data_assegnazione);
            if (form.data_ultima_revisione) fd.append("data_ultima_revisione", form.data_ultima_revisione);
            if (form.data_scadenza)         fd.append("data_scadenza", form.data_scadenza);
            if (form.periodicita_mesi)      fd.append("periodicita_mesi", form.periodicita_mesi);
            if (form.note)                  fd.append("note", form.note);
            if (file)                       fd.append("file", file);
            await SafetyDotazioniService.create(fd);
            setMsg("Dotazione registrata.");
            setShow(false);
            setForm(EMPTY);
            setFile(null);
            await loadItems();
        } catch (err) {
            setError(err?.message || "Errore nel salvataggio.");
        } finally {
            setSaving(false);
        }
    };

    const onDownload = async (d) => {
        setError(null);
        try {
            const fn = `${(d.titolo || d.tipo_denominazione || "dotazione").toString().replace(/[^\w\-]+/g, "_")}.pdf`;
            await SafetyDotazioniService.download(d.id_dotazione, fn);
        } catch (err) {
            setError(err?.message || "Impossibile scaricare il file.");
        }
    };

    const onDelete = async (d) => {
        if (!d?.id_dotazione) return;
        if (!window.confirm("Eliminare questa dotazione dal registro?")) return;
        setError(null);
        try {
            await SafetyDotazioniService.deleteDotazione(d.id_dotazione);
            await loadItems();
        } catch (err) {
            setError(err?.message || "Errore nell'eliminazione.");
        }
    };

    const tipoSel = form.id_tipo_dot ? tipi.find(t => Number(t.id_tipo_dot) === Number(form.id_tipo_dot)) : null;
    const isArma = tipoSel?.categoria === "Armi" || tipoSel?.codice?.startsWith("ARMA");

    if (!idAzienda && !idLavoratore) {
        return (
            <BsAlert variant="info" className="py-2 mb-0">
                <i className="bi bi-info-circle me-1" />
                Salva prima i dati per gestire il registro dotazioni e armamento.
            </BsAlert>
        );
    }

    const titoloPanel = soloLavoratore
        ? "Dotazioni assegnate"
        : "Registro dotazioni e armamento";

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">
                    <i className="bi bi-shield-shaded me-2 text-danger" />
                    {titoloPanel}
                </h6>
                <Button size="sm" variant="primary" onClick={openForm} disabled={show || !idAzienda}>
                    <i className="bi bi-plus-lg me-1" />
                    Aggiungi dotazione
                </Button>
            </div>

            <div className="text-muted small mb-2">
                Registro <strong>armi</strong> (matricola, calibro, revisione), <strong>giubbotti antiproiettile</strong>
                (validità balistica), <strong>radio</strong> e altre dotazioni operative, con eventuale assegnazione al lavoratore.
            </div>

            {error && <BsAlert variant="danger" className="py-2 small">{error}</BsAlert>}
            {msg && <BsAlert variant="success" className="py-2 small" onClose={() => setMsg(null)} dismissible>{msg}</BsAlert>}

            {show && tipi.length === 0 && !error && (
                <BsAlert variant="warning" className="py-2 small mb-2">
                    Catalogo vuoto. Esegui <code className="mx-1">2026-06-08_safety_dotazioni.sql</code>
                    e verifica i comparti dell'azienda.
                </BsAlert>
            )}

            {show && (
                <div className="border rounded p-3 mb-3" style={{ backgroundColor: "#f8f9fa" }}>
                    <div className="fw-semibold mb-2">
                        <i className="bi bi-shield-fill me-1 text-danger" />
                        Nuova dotazione
                    </div>
                    <Row className="g-2">
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Categoria / tipo</Form.Label>
                            <Form.Select
                                size="sm"
                                value={form.id_tipo_dot}
                                onChange={e => setForm(f => ({ ...f, id_tipo_dot: e.target.value }))}
                            >
                                <option value="">— Nessuno / altro —</option>
                                {groupByCategoria(tipi).map(([cat, catItems]) => (
                                    <optgroup key={cat} label={cat}>
                                        {catItems.map(t => (
                                            <option key={t.id_tipo_dot} value={t.id_tipo_dot}>{t.denominazione}</option>
                                        ))}
                                    </optgroup>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Descrizione (se altro)</Form.Label>
                            <Form.Control
                                size="sm"
                                value={form.titolo}
                                onChange={e => setForm(f => ({ ...f, titolo: e.target.value }))}
                                placeholder="Es. Beretta 92FS matr. …"
                            />
                        </Col>
                        {!soloLavoratore && (
                            <Col xs={12} md={6}>
                                <Form.Label className="small mb-0">Assegnato a</Form.Label>
                                <LavoratoreSearchPicker
                                    idAzienda={idAzienda}
                                    value={form.id_lavoratore}
                                    onChange={v => setForm(f => ({ ...f, id_lavoratore: v }))}
                                    allowEmpty
                                    emptyLabel="— Magazzino / non assegnato —"
                                    placeholder="Cerca lavoratore da assegnare…"
                                />
                            </Col>
                        )}
                        <Col xs={12} md={3}>
                            <Form.Label className="small mb-0">Marca</Form.Label>
                            <Form.Control size="sm" value={form.marca} onChange={e => setForm(f => ({ ...f, marca: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={3}>
                            <Form.Label className="small mb-0">Modello</Form.Label>
                            <Form.Control size="sm" value={form.modello} onChange={e => setForm(f => ({ ...f, modello: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">N. serie / matricola</Form.Label>
                            <Form.Control size="sm" value={form.numero_serie} onChange={e => setForm(f => ({ ...f, numero_serie: e.target.value }))} />
                        </Col>
                        {isArma && (
                            <Col xs={12} md={2}>
                                <Form.Label className="small mb-0">Calibro</Form.Label>
                                <Form.Control size="sm" value={form.calibro} onChange={e => setForm(f => ({ ...f, calibro: e.target.value }))} placeholder="9x21" />
                            </Col>
                        )}
                        <Col xs={12} md={isArma ? 2 : 4}>
                            <Form.Label className="small mb-0">Ubicazione</Form.Label>
                            <Form.Control size="sm" value={form.ubicazione} onChange={e => setForm(f => ({ ...f, ubicazione: e.target.value }))} placeholder="Armeria, veicolo…" />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Stato</Form.Label>
                            <Form.Select size="sm" value={form.stato_operativo} onChange={e => setForm(f => ({ ...f, stato_operativo: e.target.value }))}>
                                {STATI_OP.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </Form.Select>
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Data assegnazione</Form.Label>
                            <Form.Control type="date" size="sm" value={form.data_assegnazione} onChange={e => setForm(f => ({ ...f, data_assegnazione: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Ultima revisione</Form.Label>
                            <Form.Control type="date" size="sm" value={form.data_ultima_revisione} onChange={e => setForm(f => ({ ...f, data_ultima_revisione: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Scadenza (revisione / validità)</Form.Label>
                            <Form.Control type="date" size="sm" value={form.data_scadenza} onChange={e => setForm(f => ({ ...f, data_scadenza: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Periodicità (mesi)</Form.Label>
                            <Form.Control
                                type="number" size="sm" min="0"
                                value={form.periodicita_mesi}
                                onChange={e => setForm(f => ({ ...f, periodicita_mesi: e.target.value }))}
                                placeholder={tipoSel?.periodicita_mesi || ""}
                            />
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small mb-0">Note</Form.Label>
                            <Form.Control as="textarea" rows={2} size="sm" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Allegato (PDF)</Form.Label>
                            <Form.Control type="file" size="sm" accept=".pdf,.PDF" onChange={e => setFile(e.target.files?.[0] || null)} />
                        </Col>
                    </Row>
                    <div className="mt-2 d-flex gap-2">
                        <Button size="sm" variant="primary" onClick={submit} disabled={saving}>
                            {saving ? <Spinner size="sm" animation="border" /> : <><i className="bi bi-check-lg me-1" />Salva</>}
                        </Button>
                        <Button size="sm" variant="outline-secondary" onClick={() => setShow(false)} disabled={saving}>Annulla</Button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="text-muted small"><Spinner size="sm" animation="border" className="me-2" />Caricamento…</div>
            ) : items.length === 0 ? (
                <div className="text-muted small">
                    Nessuna dotazione registrata. Usa <strong>"Aggiungi dotazione"</strong> per censire armi, giubbotti, radio e altro.
                </div>
            ) : (
                <Table size="sm" bordered hover responsive className="mb-0">
                    <thead className="table-light">
                        <tr>
                            <th>Dotazione</th>
                            {!soloLavoratore && <th>Assegnato a</th>}
                            <th>Serie / calibro</th>
                            <th>Revisione</th>
                            <th>Scadenza</th>
                            <th>Stato</th>
                            <th className="text-end">Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(d => {
                            const statoOp = STATI_OP.find(s => s.value === d.stato_operativo)?.label || d.stato_operativo || "—";
                            return (
                                <tr key={d.id_dotazione}>
                                    <td>
                                        <div>{d.titolo || d.tipo_denominazione || "—"}</div>
                                        <div className="text-muted small">
                                            {d.tipo_categoria || ""}
                                            {d.marca || d.modello ? `${d.tipo_categoria ? " · " : ""}${[d.marca, d.modello].filter(Boolean).join(" ")}` : ""}
                                            {d.ubicazione ? ` · ${d.ubicazione}` : ""}
                                        </div>
                                    </td>
                                    {!soloLavoratore && (
                                        <td>
                                            {d.lav_cognome
                                                ? `${d.lav_cognome} ${d.lav_nome || ""}`.trim()
                                                : <span className="text-muted">Magazzino</span>}
                                        </td>
                                    )}
                                    <td>
                                        {d.numero_serie || "—"}
                                        {d.calibro ? <div className="text-muted small">{d.calibro}</div> : null}
                                    </td>
                                    <td>{fmtDate(d.data_ultima_revisione)}</td>
                                    <td>{fmtDate(d.data_scadenza)}</td>
                                    <td>
                                        {statoBadge(d.stato, d.giorni_alla_scadenza)}
                                        {d.stato_operativo && d.stato_operativo !== "OPERATIVO" && (
                                            <div className="text-muted small">{statoOp}</div>
                                        )}
                                    </td>
                                    <td className="text-end">
                                        {d.path_file && (
                                            <Button size="sm" variant="danger" className="me-1" onClick={() => onDownload(d)} title="Scarica file">
                                                <i className="bi bi-file-earmark-pdf me-1" />PDF
                                            </Button>
                                        )}
                                        <Button size="sm" variant="outline-danger" onClick={() => onDelete(d)} title="Elimina">
                                            <i className="bi bi-trash" />
                                        </Button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            )}
        </div>
    );
}
